import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, AlertCircle, Download, Calendar, MapPin, Clock, Ticket, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [orderData, setOrderData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    try {
      const response = await axios.get(`${API}/api/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Get tickets
        const ticketsResponse = await axios.get(`${API}/api/orders/${response.data.order_id}/tickets`);
        setOrderData(ticketsResponse.data);
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        // Continue polling
        if (pollCount < maxAttempts) {
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('failed');
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
      if (pollCount < maxAttempts) {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6 animate-spin" />
          <h2 className="text-2xl font-bold text-white mb-2">Verificando tu pago...</h2>
          <p className="text-gray-400">Por favor espera mientras confirmamos tu compra</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-2">Error en el pago</h2>
          <p className="text-gray-400 mb-6">
            No pudimos confirmar tu pago. Si el dinero fue debitado, por favor contáctanos.
          </p>
          <Link
            to="/eventos"
            className="inline-block px-6 py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 transition-colors"
          >
            Volver a eventos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">¡Compra exitosa!</h1>
          <p className="text-gray-400">
            Tus tickets han sido enviados a <span className="text-amber-400">{orderData?.order?.customer_email}</span>
          </p>
        </motion.div>

        {/* Order Summary */}
        {orderData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden mb-8"
          >
            {/* Event Info */}
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-start space-x-4">
                {orderData.event?.cover_image && (
                  <img 
                    src={orderData.event.cover_image} 
                    alt=""
                    className="w-24 h-24 rounded-xl object-cover"
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">
                    {orderData.event?.name?.es || orderData.order?.event_name}
                  </h2>
                  <div className="space-y-1 text-sm text-gray-400">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                      <span className="capitalize">{formatDate(orderData.event?.date)}</span>
                    </div>
                    {orderData.event?.start_time && (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-amber-500" />
                        <span>{orderData.event.start_time}</span>
                      </div>
                    )}
                    {orderData.event?.venue_name && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2 text-amber-500" />
                        <span>{orderData.event.venue_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="p-6 border-b border-zinc-800 bg-zinc-800/30">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-400 text-sm">Orden</p>
                  <p className="text-white font-mono">{orderData.order?.order_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-sm">{orderData.order?.num_attendees} x {orderData.order?.access_level_name}</p>
                  <p className="text-amber-400 font-bold text-xl">
                    ${orderData.order?.total_amount?.toLocaleString()} {orderData.order?.currency}
                  </p>
                </div>
              </div>
            </div>

            {/* Tickets */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Ticket className="w-5 h-5 mr-2 text-amber-500" />
                Tus Tickets ({orderData.tickets?.length})
              </h3>
              <div className="space-y-4">
                {orderData.tickets?.map((ticket) => (
                  <div 
                    key={ticket.ticket_id}
                    className="bg-zinc-800 rounded-xl p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-4">
                      {ticket.qr_code && (
                        <img 
                          src={`data:image/png;base64,${ticket.qr_code}`}
                          alt="QR Code"
                          className="w-20 h-20 rounded-lg bg-white p-1"
                        />
                      )}
                      <div>
                        <p className="text-white font-medium">Ticket #{ticket.attendee_number}</p>
                        <p className="text-gray-400 text-sm">{ticket.access_level_name}</p>
                        <p className="text-gray-500 text-xs font-mono">{ticket.ticket_id}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-8"
        >
          <h3 className="text-amber-400 font-bold mb-2 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            Importante
          </h3>
          <p className="text-amber-200/80 text-sm">
            El acceso al evento requiere presentar <strong>identificación oficial vigente</strong> (INE, pasaporte o licencia de conducir) junto con el código QR de confirmación. Sin estos documentos no se permitirá el acceso.
          </p>
        </motion.div>

        {/* Email Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-gray-400 text-sm"
        >
          <Mail className="w-5 h-5 mx-auto mb-2" />
          <p>Hemos enviado los tickets y el código QR a tu correo electrónico.</p>
          <p>Si no los ves, revisa tu carpeta de spam.</p>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 flex justify-center space-x-4"
        >
          <Link
            to="/eventos"
            className="px-6 py-3 bg-zinc-800 text-white rounded-xl hover:bg-zinc-700 transition-colors"
          >
            Ver más eventos
          </Link>
          <Link
            to="/"
            className="px-6 py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 transition-colors"
          >
            Ir al inicio
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
